import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBuilding } from '@fortawesome/free-solid-svg-icons'
import { PersonDto, PersonLiteDto } from '../api/person/models/PersonDto'
import {
    GenericCandidateLite,
    HelixCandidateLiteDto,
} from '../api/role/models/CandidateDto'
import { TransformedResolvedPerson } from './CandidateResultList'
import { MapExtra } from '../utils/MapExtra'
import { CompanyDto } from '../api/company/models/CompanyDto'
import {
    getProfilePicUrl,
    getCountryFlag,
    getSortedWorkedAts,
    formatText,
    formatPersonName,
} from '../utils/personUtils'
import { formatDuration } from '../utils/dateUtils'
import ExpandableText from './ExpandableText'
import LinkedInLink from './LinkedInLink'
import { HelixSearchV2TopPickBadge } from '../uiV2/helixSearchV2Copy/HelixSearchV2TopPickBadge'
import { SaveToRoleControls } from '../uiV2/helixSearchV2Copy/SaveToRoleControls'

interface Props {
    roleId?: string
    candidate: GenericCandidateLite
    person: PersonDto
    transformedResolvedPerson?: TransformedResolvedPerson
    personLiteCache: MapExtra<string, PersonLiteDto>
    companyCache: MapExtra<string, CompanyDto>
    onCandidateSelection?: (candidateId: string) => void
    renderScores?: (candidate: GenericCandidateLite) => React.ReactNode
}

export default function CandidateResultItem({
    roleId,
    candidate,
    person,
    transformedResolvedPerson,
    personLiteCache,
    companyCache,
    onCandidateSelection,
    renderScores,
}: Props) {
    const [showAllJobs, setShowAllJobs] = useState(false)
    const [expandedCompanyIds, setExpandedCompanyIds] = useState(
        new Set<string>(),
    )
    const sortedJobs = getSortedWorkedAts(person?.workedAts ?? [])
    const displayedJobs = showAllJobs ? sortedJobs : sortedJobs.slice(0, 4)
    const [helixCandidate, setHelixCandidate] = useState<
        HelixCandidateLiteDto | undefined
    >(undefined)

    useEffect(() => {
        // if this prop exists then we "know" it's a Helix candidate (well at least we know it has LLM data attached to it)
        if ('llmRelevanceScoreReason' in candidate) {
            setHelixCandidate(candidate as HelixCandidateLiteDto)
        }
    }, [candidate])

    function toggleCompany(companyId: string) {
        setExpandedCompanyIds((prev) => {
            const next = new Set(prev)
            if (next.has(companyId)) {
                next.delete(companyId)
            } else {
                next.add(companyId)
            }
            return next
        })
    }

    return (
        <div className="p-4 border-b">
            <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                {/* Avatar */}
                <div className="row-span-1">
                    <div className="flex flex-col items-center gap-2 mt-1">
                        <img
                            src={getProfilePicUrl(person, true)}
                            className="w-14 h-14 rounded-full object-cover"
                            onError={(e) => {
                                e.currentTarget.onerror = null
                                e.currentTarget.src = '/no-profile-pic.svg'
                            }}
                        />
                    </div>
                </div>

                {/* Name, headline, controls */}
                <div className="flex justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            <span
                                onClick={() =>
                                    onCandidateSelection?.(
                                        candidate.candidatePersonId,
                                    )
                                }
                                className="text-blue-800 font-semibold text-lg hover:underline cursor-pointer"
                            >
                                {formatPersonName(person)}
                            </span>
                            {person.linkedInUrl && (
                                <LinkedInLink url={person.linkedInUrl} />
                            )}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                            {person.headline}
                            <br />
                            {getCountryFlag(
                                person.location?.split(',').pop()?.trim() || '',
                            )}
                            <span className="ml-2">{person.location}</span>
                        </div>

                        {/* {roleId && (
                            <div className="mt-2">
                                <CandidateControls
                                    roleId={roleId}
                                    candidateId={candidate.candidatePersonId}
                                />
                            </div>
                        )} */}
                    </div>
                    {roleId && (
                        <SaveToRoleControls
                            roleId={roleId}
                            candidateId={candidate.candidatePersonId}
                        />
                    )}
                    {renderScores && (
                        <div className="text-xs text-gray-500 min-w-[200px]">
                            {renderScores(candidate)}
                        </div>
                    )}
                </div>

                {helixCandidate && helixCandidate.llmRelevanceScoreReason && (
                    <>
                        <Label>✨ Contxt Score</Label>
                        <Content>
                            <div className="space-y-3">
                                {/* Suitability + TOP PICK on the same line */}
                                <div className="flex items-center gap-3 text-sm">
                                    <span className="font-semibold">
                                        Suitability:
                                    </span>
                                    <span>
                                        {helixCandidate.llmRelevanceScoreScore}%
                                    </span>

                                    {helixCandidate.llmRelevanceScoreTopPick && (
                                        <HelixSearchV2TopPickBadge />
                                    )}
                                </div>

                                {/* Chat bubble for the reason */}
                                <div>
                                    <span className="font-semibold block mb-1">
                                        Reason:
                                    </span>
                                    <div className="relative inline-block bg-gray-100 text-gray-800 text-sm px-3 py-2 rounded-lg shadow-sm max-w-[520px]">
                                        {helixCandidate.llmRelevanceScoreReason}
                                        <span className="absolute left-0 top-2 w-0 h-0 border-t-8 border-t-transparent border-r-8 border-r-gray-100 border-b-8 border-b-transparent -ml-2"></span>
                                    </div>
                                </div>
                            </div>
                        </Content>
                    </>
                )}

                {/* Experience */}
                <Label>Experience</Label>
                <Content>
                    {displayedJobs.map((job) => (
                        <div key={job.id} className="mb-0.5">
                            <span className="font-bold">{job.jobTitle}</span> at{' '}
                            <span className="text-gray-900">
                                {job.company?.name}
                            </span>
                            {job.startDate && (
                                <span className="italic text-gray-500">
                                    {' '}
                                    · {new Date(
                                        job.startDate,
                                    ).getFullYear()} -{' '}
                                    {job.endDate
                                        ? new Date(job.endDate).getFullYear()
                                        : 'Present'}{' '}
                                    ·{' '}
                                    {
                                        formatDuration(
                                            job.startDate,
                                            job.endDate,
                                        ).duration
                                    }
                                </span>
                            )}
                        </div>
                    ))}
                    {sortedJobs.length > 4 && (
                        <button
                            className="text-xs text-blue-600 mt-1 hover:underline"
                            onClick={() => setShowAllJobs((p) => !p)}
                        >
                            {showAllJobs
                                ? 'Show less'
                                : `Show all (${sortedJobs.length})`}
                        </button>
                    )}
                </Content>

                {/* Network */}
                <Label>Network</Label>
                <Content>
                    {!transformedResolvedPerson?.companies?.length && (
                        <div className="text-gray-500 text-sm">-</div>
                    )}
                    {transformedResolvedPerson?.companies?.map(
                        (company, idx) => {
                            const isExpanded = expandedCompanyIds.has(
                                company.companyId,
                            )
                            const visibleConnections = isExpanded
                                ? company.connections
                                : company.connections.slice(0, 3)
                            const companyDto = companyCache.getOrThrow(
                                company.companyId,
                            )

                            return (
                                <div
                                    key={company.companyId}
                                    className={`${idx > 0 ? 'pt-2' : ''} ${
                                        idx <
                                        transformedResolvedPerson.companies
                                            .length -
                                            1
                                            ? 'border-b pb-2'
                                            : ''
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-2 ml-1">
                                        <FontAwesomeIcon
                                            icon={faBuilding}
                                            className="text-ctxt-blue"
                                        />
                                        <h3 className="text-base font-semibold">
                                            {companyDto.name}
                                        </h3>
                                    </div>
                                    <div className="text-sm text-gray-800">
                                        {visibleConnections.map((conn, i) => {
                                            const seed =
                                                personLiteCache.getOrThrow(
                                                    conn.seedPersonId,
                                                )
                                            const duration = formatDuration(
                                                conn.overallStart,
                                                conn.overallEnd,
                                            ).duration
                                            return (
                                                <div
                                                    key={i}
                                                    className="flex items-center gap-2"
                                                >
                                                    <img
                                                        src={getProfilePicUrl(
                                                            seed,
                                                            true,
                                                        )}
                                                        className="rounded-full w-6 h-6 mt-0.5"
                                                        onError={(e) => {
                                                            e.currentTarget.onerror =
                                                                null
                                                            e.currentTarget.src =
                                                                '/no-profile-pic.svg'
                                                        }}
                                                    />
                                                    <div>
                                                        <span className="font-medium">
                                                            {formatPersonName(
                                                                seed,
                                                            )}
                                                        </span>{' '}
                                                        |{' '}
                                                        {conn.seedPersonJobTitles.join(
                                                            ' | ',
                                                        )}
                                                        <span className="text-gray-500 italic">
                                                            {' '}
                                                            ·{' '}
                                                            {new Date(
                                                                conn.overallStart,
                                                            ).getFullYear()}{' '}
                                                            -{' '}
                                                            {conn.overallEnd
                                                                ? new Date(
                                                                      conn.overallEnd,
                                                                  ).getFullYear()
                                                                : 'Present'}{' '}
                                                            · {duration}
                                                        </span>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        {company.connections.length > 3 && (
                                            <button
                                                className="text-xs text-blue-600 mt-1 hover:underline"
                                                onClick={() =>
                                                    toggleCompany(
                                                        company.companyId,
                                                    )
                                                }
                                            >
                                                {isExpanded
                                                    ? 'Show less'
                                                    : `Show all (${company.connections.length})`}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )
                        },
                    )}
                </Content>

                {/* About */}
                <Label>About</Label>
                <Content>
                    <ExpandableText
                        className="text-sm whitespace-pre-line text-gray-600"
                        text={formatText(person.summary ?? '-')}
                        maxLines={4}
                    />
                </Content>
            </div>
        </div>
    )
}

function Label({ children }: { children: React.ReactNode }) {
    return (
        <div className="text-sm font-bold text-gray-600 pt-2 text-right mr-4">
            {children}
        </div>
    )
}

function Content({ children }: { children: React.ReactNode }) {
    return <div className="text-sm text-gray-800 pt-2">{children}</div>
}
